import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Banknote,
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  DollarSign,
  Info,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const PERIODS = [
  { key: "THIS_MONTH", label: "This Month" },
  { key: "LAST_MONTH", label: "Last Month" },
  { key: "YTD", label: "Year to Date" },
  { key: "CUSTOM", label: "Custom" },
  { key: "ALL_TIME", label: "All Time" },
];

const PERIOD_TOOLTIP =
  "Filters Commission History by Accrued Date using AFP's U.S. Eastern Time reporting calendar—not by payment date. A commission may be paid later after its 30-day hold and the applicable monthly payment cycle.";

const PAYMENT_DATE_TOOLTIP =
  "AFP runs one Sales Affiliate commission payment cycle per month. A commission that clears its 30-day hold during a calendar month is scheduled for payment on the 15th of the following month. If that date is not a Federal Reserve Bank business day, processing moves to the next Federal Reserve Bank business day. Dates use U.S. Eastern Time. Payment remains subject to payout readiness, adjustments, and Program Terms.";

const SUMMARY_CARDS = [
  {
    key: "held_minor",
    label: "Accrued — In Hold Period",
    description: "Commission accrued and currently recorded within the 30-day hold workflow.",
    icon: Clock3,
    iconWrap: "bg-blue-50 text-blue-600 ring-blue-100",
    emphasis: true,
  },
  {
    key: "approved_minor",
    label: "Approved for Payment",
    description: "Commission approved for inclusion in a payment cycle.",
    icon: CheckCircle2,
    iconWrap: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  },
  {
    key: "paid_ytd_minor",
    label: "Paid Year to Date",
    description: "Actual commission payments recorded in the current calendar year.",
    icon: DollarSign,
    iconWrap: "bg-violet-50 text-violet-600 ring-violet-100",
  },
  {
    key: "adjustments_minor",
    label: "Adjustments",
    description: "Refund, chargeback, clawback, or correction activity.",
    icon: RotateCcw,
    iconWrap: "bg-orange-50 text-orange-600 ring-orange-100",
  },
];

const SORTERS = {
  customer: (entry) =>
    `${entry.subscriber_name || ""} ${entry.company_name || ""}`.toLowerCase(),
  commission_type: (entry) => entry.commission_type || "",
  gross_sale_minor: (entry) => Number(entry.gross_sale_minor) || 0,
  eligible_revenue_minor: (entry) => Number(entry.eligible_revenue_minor) || 0,
  rate_bps: (entry) => Number(entry.rate_bps) || 0,
  commission_amount_minor: (entry) => Number(entry.commission_amount_minor) || 0,
  accrued_date_et: (entry) => entry.accrued_date_et || "",
  hold_until_date_et: (entry) => entry.hold_until_date_et || "",
  status: (entry) => entry.status || "",
  adjustments_minor: (entry) => Number(entry.adjustments_minor) || 0,
};

function money(minor, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format((Number(minor) || 0) / 100);
  } catch {
    return `${currency || ""} ${((Number(minor) || 0) / 100).toFixed(2)}`.trim();
  }
}

function formatDate(value) {
  if (!value) return "—";

  const normalized =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T12:00:00`
      : value;

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

function parseYmd(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const [year, month, day] = value.split("-").map(Number);
  return { year, month, day };
}

function ymdUtc(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toISOString().slice(0, 10);
}

function lastDayOfMonthYmd(year, month) {
  const date = new Date(Date.UTC(year, month, 0));
  return date.toISOString().slice(0, 10);
}

function rangeForPeriod(period, asOfEt, customStart, customEnd) {
  const parsed = parseYmd(asOfEt);
  if (!parsed || period === "ALL_TIME") return { start: null, end: null };

  if (period === "THIS_MONTH") {
    return {
      start: ymdUtc(parsed.year, parsed.month, 1),
      end: asOfEt,
    };
  }

  if (period === "LAST_MONTH") {
    const currentMonthStart = new Date(
      Date.UTC(parsed.year, parsed.month - 1, 1),
    );
    currentMonthStart.setUTCMonth(currentMonthStart.getUTCMonth() - 1);

    const year = currentMonthStart.getUTCFullYear();
    const month = currentMonthStart.getUTCMonth() + 1;

    return {
      start: ymdUtc(year, month, 1),
      end: lastDayOfMonthYmd(year, month),
    };
  }

  if (period === "YTD") {
    return {
      start: ymdUtc(parsed.year, 1, 1),
      end: asOfEt,
    };
  }

  return {
    start: customStart || null,
    end: customEnd || null,
  };
}

function commissionTypeLabel(value) {
  if (value === "INITIAL_SALE") return "Initial Sale";
  if (value === "RENEWAL") return "Monthly Renewal";

  return String(value || "Commission")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function statusLabel(value) {
  if (value === "HELD") return "IN HOLD";
  if (value === "APPROVED") return "APPROVED";
  if (value === "PAID") return "PAID";
  if (value === "REVERSED") return "REVERSED";
  return value || "—";
}

function statusClasses(value) {
  if (value === "PAID") return "bg-emerald-50 text-emerald-800";
  if (value === "APPROVED") return "bg-blue-50 text-blue-800";
  if (value === "HELD") return "bg-amber-50 text-amber-800";
  if (value === "REVERSED") return "bg-red-50 text-red-800";
  return "bg-slate-100 text-slate-700";
}

function getPaymentPresentation(requirements) {
  const payment = (requirements || []).find((item) => item.key === "payment");

  if (!payment) {
    return {
      title: "Payment enrollment status unavailable",
      body: "Your payment-enrollment status could not be loaded.",
      badge: "Status unavailable",
      tone: "slate",
      action: false,
    };
  }

  const state = payment.presentation_state || "NOT_CONFIGURED";

  if (payment.verified_for_payment || state === "VERIFIED") {
    return {
      title: "Payment enrollment verified",
      body: "Your payment details are verified and approved commissions may be paid.",
      badge: "Payouts enabled",
      tone: "emerald",
      action: false,
    };
  }

  if (state === "SUBMITTED_PENDING_VERIFICATION") {
    return {
      title: "Payment enrollment awaiting verification",
      body: "Commission activity may accrue, but approved payments remain pending until verification is complete.",
      badge: "Verification pending",
      tone: "blue",
      action: false,
    };
  }

  if (!payment.provider_configured || state === "NOT_CONFIGURED") {
    return {
      title: "Commission payment enrollment is not yet available",
      body: "Commission activity will continue to be recorded. Payments begin after the secure provider workflow is configured and verified.",
      badge: "Not yet available",
      tone: "amber",
      action: false,
    };
  }

  if (state === "FAILED") {
    return {
      title: "Payment enrollment needs attention",
      body: "Restart payment enrollment before approved commissions can be paid.",
      badge: "Action required",
      tone: "red",
      action: true,
    };
  }

  return {
    title: "Complete payment enrollment",
    body: "Complete payment enrollment to start receiving eligible commission payments.",
    badge: "Action required",
    tone: "amber",
    action: true,
  };
}

function toneClasses(tone) {
  if (tone === "emerald")
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (tone === "blue") return "border-blue-200 bg-blue-50 text-blue-800";
  if (tone === "red") return "border-red-200 bg-red-50 text-red-800";
  if (tone === "amber") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function InfoTip({ text, align = "right" }) {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label="More information"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
      >
        <Info size={14} />
      </button>

      <span
        role="tooltip"
        className={`pointer-events-none absolute top-7 z-30 hidden w-80 rounded-xl bg-slate-950 px-4 py-3 text-left text-xs font-semibold leading-5 text-white shadow-xl group-hover:block group-focus-within:block ${
          align === "left" ? "left-0" : "right-0"
        }`}
      >
        {text}
      </span>
    </span>
  );
}

function SortHeader({
  sortKey,
  activeKey,
  direction,
  onSort,
  children,
}) {
  const active = activeKey === sortKey;
  const Icon = active
    ? direction === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;

  return (
    <th className="px-3 py-4 align-top">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1.5 text-left hover:text-blue-700"
      >
        <span>{children}</span>
        <Icon size={12} />
      </button>
    </th>
  );
}

export default function SalesAffiliateCommissionExperience({
  requirements,
}) {
  const payment = getPaymentPresentation(requirements);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ledger, setLedger] = useState({
    summary_by_currency: [],
    entries: [],
    reporting_as_of_date_et: null,
    payout_policy: null,
    next_payment_date: null,
  });

  const [period, setPeriod] = useState("YTD");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortKey, setSortKey] = useState("accrued_date_et");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");

      const { data, error: loadError } = await supabase.rpc(
        "get_my_sales_affiliate_earnings",
      );

      if (!alive) return;

      if (loadError || !data?.ok) {
        setError(
          "Unable to load commission activity right now. Please try again.",
        );
        setLedger({
          summary_by_currency: [],
          entries: [],
          reporting_as_of_date_et: null,
          payout_policy: null,
          next_payment_date: null,
        });
      } else {
        setLedger(data);
      }

      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, []);

  const currencies = ledger.summary_by_currency || [];
  const entries = useMemo(() => ledger.entries || [], [ledger.entries]);

  const filteredEntries = useMemo(() => {
    const { start, end } = rangeForPeriod(
      period,
      ledger.reporting_as_of_date_et,
      customStart,
      customEnd,
    );

    return entries.filter((entry) => {
      const accrued = entry.accrued_date_et;
      if (!accrued) return false;
      if (start && accrued < start) return false;
      if (end && accrued > end) return false;
      return true;
    });
  }, [
    entries,
    period,
    ledger.reporting_as_of_date_et,
    customStart,
    customEnd,
  ]);

  const sortedEntries = useMemo(() => {
    const getter = SORTERS[sortKey] || SORTERS.accrued_date_et;

    return [...filteredEntries].sort((a, b) => {
      const av = getter(a);
      const bv = getter(b);

      if (av === bv) return 0;

      const result = av > bv ? 1 : -1;
      return sortDirection === "asc" ? result : -result;
    });
  }, [filteredEntries, sortKey, sortDirection]);

  const handleSort = (nextKey) => {
    if (sortKey === nextKey) {
      setSortDirection((current) =>
        current === "asc" ? "desc" : "asc",
      );
    } else {
      setSortKey(nextKey);
      setSortDirection("asc");
    }
  };

  return (
    <section
      id="commission"
      className="mt-7 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-7 sm:py-7"
    >
      <div className="flex flex-col items-center text-center">
        <h2 className="text-3xl font-black tracking-tight text-slate-950">
          Earnings &amp; Payouts
        </h2>

        <div className="mt-4 w-full max-w-[760px] rounded-2xl border border-blue-100 bg-blue-50 px-6 py-3.5">
          <div className="relative flex items-center justify-center gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-blue-600 ring-1 ring-blue-100">
              <CalendarDays size={22} />
            </div>

            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-700">
                Next Commission Payment Date
              </div>

              <div className="mt-1 text-2xl font-black leading-none text-slate-950">
                {loading || error
                  ? "—"
                  : formatDate(ledger.next_payment_date)}
              </div>

              <div className="mt-1.5 text-xs font-semibold leading-4 text-slate-600 lg:whitespace-nowrap">
                {loading
                  ? "Loading commission activity…"
                  : error
                    ? "Unavailable while commission data could not be loaded"
                    : ledger.next_payment_date
                      ? "Scheduled AFP payment cycle; subject to payout readiness"
                      : "No unpaid commission activity"}
              </div>
            </div>

            <div className="absolute right-0 top-0">
              <InfoTip text={PAYMENT_DATE_TOOLTIP} />
            </div>
          </div>
        </div>

        <p className="mt-4 max-w-[1100px] text-sm leading-6 text-slate-600 lg:whitespace-nowrap">
          Commission is calculated on eligible customer revenue actually collected, excluding taxes, refunds, chargebacks, credits, and payment-processing fees.
        </p>
      </div>

      {error ? (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-800">
          {error}
        </div>
      ) : null}

      <div className="mt-7">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-8 text-sm font-semibold text-slate-500">
            Loading commission balances…
          </div>
        ) : error ? null : currencies.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-8 text-sm font-semibold text-slate-600">
            No qualifying commission activity has been recorded yet.
            Balances will appear by transaction currency when activity is
            recorded.
          </div>
        ) : (
          <div className="space-y-5">
            {currencies.map((currencyRow) => (
              <div key={currencyRow.currency}>
                {currencies.length > 1 ? (
                  <div className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                    {currencyRow.currency} balances
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {SUMMARY_CARDS.map((card) => {
                    const Icon = card.icon;

                    return (
                      <div
                        key={`${currencyRow.currency}-${card.key}`}
                        className={`rounded-2xl bg-slate-50 px-5 py-5 ${
                          card.emphasis
                            ? "border-2 border-emerald-300"
                            : "border border-slate-200"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-1 ${card.iconWrap}`}
                          >
                            <Icon size={23} strokeWidth={2.2} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="text-[11px] font-black uppercase leading-4 tracking-[0.16em] text-slate-500">
                              {card.label}
                            </div>

                            <div className="mt-2 text-3xl font-black text-slate-950">
                              {money(
                                currencyRow[card.key],
                                currencyRow.currency,
                              )}
                            </div>

                            <p className="mt-2 text-xs leading-5 text-slate-600">
                              {card.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <section className="mt-7 overflow-visible rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-600">
                Commission Activity
              </div>

              <h3 className="mt-1 text-2xl font-black text-slate-950">
                Commission History
              </h3>
            </div>

            <div className="flex flex-col items-start gap-2 lg:items-end">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  {PERIODS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      title={PERIOD_TOOLTIP}
                      onClick={() => setPeriod(item.key)}
                      className={`border-r border-slate-200 px-4 py-2 text-xs font-black last:border-r-0 ${
                        period === item.key
                          ? "bg-blue-600 text-white"
                          : "text-slate-600 hover:bg-white hover:text-blue-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                <InfoTip text={PERIOD_TOOLTIP} />
              </div>

              <div className="text-xs font-semibold text-slate-500">
                Showing {sortedEntries.length}{" "}
                {sortedEntries.length === 1
                  ? "transaction"
                  : "transactions"}
              </div>
            </div>
          </div>

          {period === "CUSTOM" ? (
            <div className="mt-4 flex flex-wrap gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <label className="text-xs font-black text-slate-700">
                Accrued from
                <input
                  type="date"
                  value={customStart}
                  onChange={(event) =>
                    setCustomStart(event.target.value)
                  }
                  className="mt-1 block rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold"
                />
              </label>

              <label className="text-xs font-black text-slate-700">
                Accrued through
                <input
                  type="date"
                  value={customEnd}
                  onChange={(event) =>
                    setCustomEnd(event.target.value)
                  }
                  className="mt-1 block rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold"
                />
              </label>
            </div>
          ) : null}
        </div>

        <div className="overflow-x-auto xl:overflow-x-visible">
          <table className="w-full min-w-[980px] text-left xl:min-w-0 xl:table-fixed">
            <colgroup>
              <col className="w-[13%]" />
              <col className="w-[11%]" />
              <col className="w-[9%]" />
              <col className="w-[10%]" />
              <col className="w-[7%]" />
              <col className="w-[11%]" />
              <col className="w-[10%]" />
              <col className="w-[9%]" />
              <col className="w-[8%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="bg-slate-50 text-[9px] font-black uppercase tracking-[0.10em] text-slate-500">
              <tr>
                <SortHeader
                  sortKey="customer"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Customer
                </SortHeader>

                <SortHeader
                  sortKey="commission_type"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Commission Type
                </SortHeader>

                <SortHeader
                  sortKey="gross_sale_minor"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Gross Sale
                </SortHeader>

                <SortHeader
                  sortKey="eligible_revenue_minor"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Eligible Revenue
                </SortHeader>

                <SortHeader
                  sortKey="rate_bps"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Rate
                </SortHeader>

                <SortHeader
                  sortKey="commission_amount_minor"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Commission Amount
                </SortHeader>

                <SortHeader
                  sortKey="accrued_date_et"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Accrued Date
                </SortHeader>

                <SortHeader
                  sortKey="hold_until_date_et"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Hold Until
                </SortHeader>

                <SortHeader
                  sortKey="status"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Status
                </SortHeader>

                <SortHeader
                  sortKey="adjustments_minor"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  Adjustment / Clawback
                </SortHeader>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-12 text-center text-slate-500"
                  >
                    Loading commission activity…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-12 text-center text-red-700"
                  >
                    Commission activity is unavailable while the error
                    above is present.
                  </td>
                </tr>
              ) : sortedEntries.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-12 text-center text-slate-500"
                  >
                    No commission activity matches the selected
                    accrued-date period.
                  </td>
                </tr>
              ) : (
                sortedEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-3 py-4">
                      <div className="font-bold text-slate-950">
                        {entry.subscriber_name || "Customer"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {entry.company_name || "—"}
                      </div>
                    </td>

                    <td className="px-3 py-4 font-semibold">
                      {commissionTypeLabel(entry.commission_type)}
                    </td>

                    <td className="px-3 py-4">
                      {money(
                        entry.gross_sale_minor,
                        entry.currency,
                      )}
                    </td>

                    <td className="px-3 py-4">
                      {money(
                        entry.eligible_revenue_minor,
                        entry.currency,
                      )}
                    </td>

                    <td className="px-3 py-4">
                      {(
                        (Number(entry.rate_bps) || 0) / 100
                      ).toFixed(2)}
                      %
                    </td>

                    <td className="px-3 py-4 font-black">
                      {money(
                        entry.commission_amount_minor,
                        entry.currency,
                      )}
                    </td>

                    <td className="px-3 py-4">
                      {formatDate(entry.accrued_date_et)}
                    </td>

                    <td className="px-3 py-4">
                      {formatDate(entry.hold_until_date_et)}
                    </td>

                    <td className="px-3 py-4">
                      <span
                        className={`inline-flex min-w-[64px] items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-black ${statusClasses(
                          entry.status,
                        )}`}
                      >
                        {statusLabel(entry.status)}
                      </span>
                    </td>

                    <td className="px-3 py-4">
                      {money(
                        entry.adjustments_minor,
                        entry.currency,
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="mt-7 grid gap-5 xl:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                <CreditCard size={20} />
              </div>
              <h3 className="text-xl font-black text-slate-950">
                Payment Enrollment
              </h3>
            </div>

            <div
              className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] ${toneClasses(
                payment.tone,
              )}`}
            >
              {payment.badge}
            </div>
          </div>

          <div
            className={`mt-5 rounded-2xl border px-5 py-5 ${toneClasses(
              payment.tone,
            )}`}
          >
            <div className="text-sm font-black">
              {payment.title}
            </div>

            <p className="mt-2 text-sm leading-6">
              {payment.body}
            </p>

            {payment.action ? (
              <div>
                <Link
                  to="/sales-affiliate/onboarding#payment"
                  className="mt-4 inline-flex text-xs font-black underline underline-offset-2"
                >
                  Go to Payment Setup →
                </Link>
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
              <CalendarDays size={20} />
            </div>
            <h3 className="text-xl font-black text-slate-950">
              Program Terms Reference
            </h3>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                Initial Sale
              </div>
              <div className="mt-2 text-3xl font-black text-slate-950">
                20%
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                Monthly Renewal
              </div>
              <div className="mt-2 text-3xl font-black text-slate-950">
                10%
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs font-semibold leading-5 text-slate-500">
            Reference rates shown are 20% for an Initial Sale and 10%
            for a qualifying Monthly Renewal. Each commission record
            preserves the rate actually applied to that transaction.
            Commissions remain subject to the 30-day hold. AFP runs one
            commission payment cycle per month on the 15th of the
            following month, or the next Federal Reserve Bank business
            day when required, using U.S. Eastern Time and subject to
            payment eligibility and the Program Terms.
          </p>
        </div>
      </div>
    </section>
  );
}
